// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:lang\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"lang\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/:notFound*\",\"regex\":\"^/([^/]+)/(.+)$\",\"paramNames\":[\"lang\",\"notFound\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/course/:courseLandingPath\",\"regex\":\"^/([^/]+)/course/([^/]+)$\",\"paramNames\":[\"lang\",\"courseLandingPath\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/examples\",\"regex\":\"^/([^/]+)/examples$\",\"paramNames\":[\"lang\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/examples/buggy-page/:slug*?\",\"regex\":\"^/([^/]+)/examples/buggy-page(?:/(.*))?$\",\"paramNames\":[\"lang\",\"slug\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/examples/isr-page\",\"regex\":\"^/([^/]+)/examples/isr-page$\",\"paramNames\":[\"lang\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/examples/sentry-example-page\",\"regex\":\"^/([^/]+)/examples/sentry-example-page$\",\"paramNames\":[\"lang\"],\"hasOptionalPrefix\":true},{\"path\":\"/:lang/examples/ssr-page\",\"regex\":\"^/([^/]+)/examples/ssr-page$\",\"paramNames\":[\"lang\"],\"hasOptionalPrefix\":true}],\"staticRoutes\":[],\"isrRoutes\":[\"/:lang/examples/buggy-page/:slug*?\"]}";globalThis["_sentryNextJsVersion"] = "16.1.2";import {getSentryOptions} from './lib/sentry';

import * as Sentry from '@sentry/nextjs';
import {getPageKeyForPathname} from '@udemy-v2/nextjs-pagekeys';
import {pageKeyDefinitions} from './generated/pagekeys.config';

const isSentryEnabled = process.env.DEPLOY_ENV !== 'local';

if (isSentryEnabled) {
    Sentry.init(
        getSentryOptions({
            // <<Stencil::Block(sentryClientCustomConfig)>>
            // <</Stencil::Block>>
        }),
    );
}

// ---------------------------------------------------------------------------
// React Hydration Mismatch Monitoring
// ---------------------------------------------------------------------------
// React 19 throws hydration errors as actual Error objects in both dev and
// prod builds. They surface as "Uncaught Error" in the browser — NOT through
// console.error. Next.js doesn't expose onRecoverableError, so we listen for
// uncaught errors via the window 'error' event.
//
// - Production: "Minified React error #418" / "#423"
// - Development: "Hydration failed because..." / "Text content did not match..."
// ---------------------------------------------------------------------------

/** Checks if an error message indicates a React hydration mismatch. */
function isHydrationMismatch(message: string): boolean {
    return (
        // Production: minified error codes
        message.includes('Minified React error #418') ||
        message.includes('Minified React error #422') ||
        message.includes('Minified React error #423') ||
        message.includes('Minified React error #425') ||
        // Development: descriptive messages
        message.includes('Text content did not match') ||
        message.includes('Hydration failed because')
    );
}

window.addEventListener('error', (event) => {
    const message = event.message || (event.error instanceof Error ? event.error.message : '');

    if (!isHydrationMismatch(message)) {
        return;
    }

    // Resolve pageKey from pathname — pure function, no async, available immediately
    const pageKey =
        getPageKeyForPathname({
            requestPathname: window.location.pathname,
            pageKeyDefinitions,
        }) ?? 'unknown';

    // Log to DataDog — not subject to Sentry's sample rate, captures 100% of occurrences
    try {
        fetch(`/${process.env.APP_NAME}/api/log/`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                level: 'error',
                logType: 'hydration_mismatch',
                message: 'React hydration mismatch detected',
                context: {
                    reactError: message,
                    pageKey,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                },
            }),
        }).catch(function (logError) {
            console.error('[Hydration Monitor] Failed to log to DataDog:', logError);
        });
    } catch (err) {
        console.error('[Hydration Monitor] Failed to prepare log payload:', err);
    }

    if (isSentryEnabled) {
        Sentry.addBreadcrumb({
            category: 'hydration',
            message: message,
            level: 'warning',
            data: {
                url: window.location.href,
                pageKey,
            },
        });
        Sentry.captureMessage('React hydration mismatch detected', {
            level: 'warning',
            tags: {
                error_type: 'hydration_mismatch',
                page_key: pageKey,
            },
            extra: {
                reactError: message,
                pageKey,
                url: window.location.href,
            },
        });
    } else {
        console.warn('[Hydration Monitor] Hydration mismatch detected:', message);
    }
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
